import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import { InfoModal } from 'components/Modal';
import { CreateStaffDto } from 'service/dto/staff.dto';
import { services } from 'service';
import { useCreateStaff } from 'common/queries-and-mutations/admin/staff';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function CreateStaffModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useCreateStaff();

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: ''
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required').nullable(),
      lastName: Yup.string().required('Last name is required').nullable(),
      phoneNumber: Yup.string().required('Phone number is required').nullable(),
      email: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .nullable()
        .test('is-allowed', 'Email already in use', async function (value) {
          if (value) {
            const isInUse =
              await services.manageStaffService.checkIfEmailInUse(value);
            return !isInUse;
          }
          return true;
        })
    }),
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onSubmit(values);
    }
  });

  const onSubmit = (formValue: CreateStaffDto) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  return (
    <InfoModal width="w-[55%]" className="h-fit rounded-md pb-9">
      <div>
        <div className="flex flex-row items-center justify-between border-b p-3">
          <p className="text-base font-medium text-[#34475E]">
            Add Staff Information
          </p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-10"
          onSubmit={formik.handleSubmit}
        >
          <div className="mb-1 mt-8">
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name="email"
                  placeholder=""
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Email"
                  error={formik.touched.email ? formik.errors.email : ''}
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name="firstName"
                  placeholder=""
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="First Name"
                  error={
                    formik.touched.firstName ? formik.errors.firstName : ''
                  }
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name="lastName"
                  placeholder=""
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Last Name"
                  error={formik.touched.lastName ? formik.errors.lastName : ''}
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name="phoneNumber"
                  placeholder=""
                  value={formik.values.phoneNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Phone Number"
                  error={
                    formik.touched.phoneNumber ? formik.errors.phoneNumber : ''
                  }
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
            </div>
          </div>

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-2 w-[9rem] rounded-[2px] p-[0.3rem]"
              label="Create Staff"
              type="submit"
              disabled={!formik.isValid || isSubmitting}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
